import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { CalendarDays } from 'lucide-react';

interface ProfileInfoStepProps {
  onProfileComplete: (profileData: any) => void;
  isLoading: boolean;
}

export const ProfileInfoStep: React.FC<ProfileInfoStepProps> = ({
  onProfileComplete,
  isLoading
}) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    dob: '',
    marketingOptIn: false,
    agreeToTerms: false
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }
    
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }
    
    if (formData.dob) {
      const birthDate = new Date(formData.dob);
      const today = new Date();
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }
      
      if (age < 18) {
        newErrors.dob = 'You must be at least 18 years old to apply for life insurance';
      }
    }
    
    if (!formData.agreeToTerms) {
      newErrors.agreeToTerms = 'You must agree to the Terms of Service and Privacy Policy';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData({ ...formData, [field]: value });
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      onProfileComplete(formData);
    }
  };

  const isFormValid = formData.firstName.trim() && formData.lastName.trim() && formData.agreeToTerms && Object.keys(errors).length === 0;

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-center">About you</h3>
        
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label htmlFor="firstName" className="text-sm font-medium">
              First name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="firstName"
              type="text"
              placeholder="John"
              value={formData.firstName}
              onChange={(e) => handleInputChange('firstName', e.target.value)}
              className={errors.firstName ? 'border-red-500' : ''}
              required
              aria-describedby={errors.firstName ? 'firstName-error' : undefined}
            />
            {errors.firstName && (
              <p id="firstName-error" className="text-sm text-red-600" role="alert" aria-live="polite">
                {errors.firstName}
              </p>
            )}
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="lastName" className="text-sm font-medium">
              Last name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="lastName"
              type="text"
              placeholder="Doe"
              value={formData.lastName}
              onChange={(e) => handleInputChange('lastName', e.target.value)}
              className={errors.lastName ? 'border-red-500' : ''}
              required
              aria-describedby={errors.lastName ? 'lastName-error' : undefined}
            />
            {errors.lastName && (
              <p id="lastName-error" className="text-sm text-red-600" role="alert" aria-live="polite">
                {errors.lastName}
              </p>
            )}
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="dob" className="text-sm font-medium">
            Date of birth (optional)
          </Label>
          <div className="relative">
            <Input
              id="dob"
              type="date"
              value={formData.dob}
              onChange={(e) => handleInputChange('dob', e.target.value)}
              className={`pr-10 ${errors.dob ? 'border-red-500' : ''}`}
              aria-describedby={errors.dob ? 'dob-error' : 'dob-help'}
            />
            <CalendarDays className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
          </div>
          {errors.dob ? (
            <p id="dob-error" className="text-sm text-red-600" role="alert" aria-live="polite">
              {errors.dob}
            </p>
          ) : (
            <p id="dob-help" className="text-xs text-muted-foreground">
              Helps us provide more accurate insurance quotes
            </p>
          )}
        </div>
      </div>

      <div className="space-y-4">
        <div className="flex items-start space-x-2">
          <Checkbox
            id="marketing"
            checked={formData.marketingOptIn}
            onCheckedChange={(checked) => 
              setFormData({ ...formData, marketingOptIn: checked as boolean })
            }
          />
          <Label htmlFor="marketing" className="text-sm text-muted-foreground leading-relaxed">
            Send me helpful tips and updates about life insurance (optional)
          </Label>
        </div>

        <div className="flex items-start space-x-2">
          <Checkbox
            id="terms"
            checked={formData.agreeToTerms}
            onCheckedChange={(checked) => {
              setFormData({ ...formData, agreeToTerms: checked as boolean });
              if (errors.agreeToTerms && checked) {
                setErrors({ ...errors, agreeToTerms: '' });
              }
            }}
            aria-describedby={errors.agreeToTerms ? 'terms-error' : undefined}
          />
          <div className="space-y-1">
            <Label htmlFor="terms" className="text-sm text-muted-foreground leading-relaxed">
              I agree to the{' '}
              <span className="text-primary underline cursor-pointer">Terms of Service</span>
              {' '}and{' '}
              <span className="text-primary underline cursor-pointer">Privacy Policy</span>
              <span className="text-red-500"> *</span>
            </Label>
            {errors.agreeToTerms && (
              <p id="terms-error" className="text-sm text-red-600" role="alert" aria-live="polite">
                {errors.agreeToTerms}
              </p>
            )}
          </div>
        </div>
      </div>

      <Button 
        type="submit" 
        className="w-full" 
        disabled={!isFormValid || isLoading}
      >
        {isLoading ? 'Creating your profile...' : 'Finish'}
      </Button>
    </form>
  );
};